import { memo, useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserDto } from '@/services/ws-permissions/src/main/resources/rest-api';
import type { ApiError, ProblemDetailsSchema } from '@/js/libs/api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNavigate } from '@/js/libs/router';
import { Loader } from '@/js/libs/ui';
import { useNotification } from '@/js/libs/ui/src/V2';
import {
  useAuth,
  useImpersonateUser,
  useSelectCompany,
  type CompanyIdentifiersFragment,
  type UserIdentifiersFragment,
} from '@/js/libs/use-auth';
import { parseError } from '@/js/libs/use-request';

import { useCleanupSessionStorageItems } from 'src/hooks/useCleanupSessionStorageItems.ts';
import { ErrorCode } from 'src/types.ts';
import { useRevertImpersonation } from '../../../hooks/index.ts';
import { IdentityErrorPage } from '../components/index.ts';
import { useGetIdentity } from '../hooks/index.ts';

type Selection = CompanyIdentifiersFragment | UserIdentifiersFragment | null;

// Replaces the identity of the logged user with the identity of the impersonated user or of the selected company, and
// keeps the children behind a loader until the identity matches the selection.
export const Impersonation = memo(function Impersonation({ children }: PropsWithChildren) {
  const [t] = useTranslation(['common']);
  const notify = useNotification();
  const navigate = useNavigate();
  const { identity, impersonatedUser, loggedUser, selectedCompany, setIdentity } = useAuth();
  const selectCompany = useSelectCompany();
  const impersonateUser = useImpersonateUser();
  const revertImpersonation = useRevertImpersonation();
  const cleanupSessionStorageItems = useCleanupSessionStorageItems();
  const getIdentity = useGetIdentity();
  const [loadError, setLoadError] = useState<ApiError<typeof ProblemDetailsSchema>>();
  // Numbers the identity loads of the selection: a reload after a failed load starts the next one.
  const [loadAttempt, setLoadAttempt] = useState(0);
  // The selection the open page and the list states belong to: the one the session restores, then every applied one.
  const pageSelectionRef = useRef<Selection>(impersonatedUser ?? selectedCompany);

  // The logged user is the identity only without a selection. A change to it under a selection, such as a saved profile,
  // leaves the identity of the selection as it is: ws-permissions takes the change over only after a while, so loading
  // that identity again could bring it back without the change.
  const loggedUserIdentity = impersonatedUser || selectedCompany ? undefined : loggedUser;

  // The identity matches the selection: the impersonated user's, the selected company's, or the logged user's without
  // either.
  const isIdentityReady = impersonatedUser
    ? identity?.email === impersonatedUser.email
    : selectedCompany
      ? identity?.company.id === selectedCompany.id
      : identity === loggedUser;

  // Applies the identity of a selection. Applying a selection other than the one the open page belongs to switches the
  // identity: the home page opens, since a page of the previous identity may be out of reach in the new one, and the
  // list states the pages keep in session storage are dropped.
  const applySelectionIdentity = useCallbackRef((selection: Selection, selectionIdentity: UserDto) => {
    const isIdentitySwitch = pageSelectionRef.current !== selection;
    pageSelectionRef.current = selection;
    setIdentity(selectionIdentity);
    if (isIdentitySwitch) {
      cleanupSessionStorageItems();
      navigate('/');
    }
  });

  // Takes the identity loaded for a company only when it carries that company. An identity of another company means
  // ws-permissions ignored the switch, resolving a deactivated or ungranted company to the user's own one without an
  // error: the selection is cleared and the user is warned.
  const handleCompanyIdentityLoaded = useCallbackRef((requestedCompany: CompanyIdentifiersFragment, loadedIdentity: UserDto) => {
    if (loadedIdentity.company.id === requestedCompany.id) {
      applySelectionIdentity(requestedCompany, loadedIdentity);
    } else {
      selectCompany(null);
      notify.warn({ body: t('messages:COMPANY_SWITCH_REVERTED', { companyName: requestedCompany.name }) });
    }
  });

  // Takes the identity loaded for an impersonated user only when it is that user's. Another identity means the request
  // went out without the impersonation, the backend ignoring the header or the stored user missing the email: the
  // impersonation is ended and the admin is warned.
  const handleImpersonatedIdentityLoaded = useCallbackRef((requestedUser: UserIdentifiersFragment, loadedIdentity: UserDto) => {
    if (loadedIdentity.email === requestedUser.email) {
      applySelectionIdentity(requestedUser, loadedIdentity);
    } else {
      revertImpersonation(requestedUser.email);
    }
  });

  // Loads the identity of the selection again after a failed load, behind the loader.
  const handleReloadClick = useCallbackRef(() => {
    setLoadError(undefined);
    setLoadAttempt((attempt) => attempt + 1);
  });

  // Clears the selection whose identity failed to load, returning the user to their own identity.
  const handleBackToOwnIdentityClick = useCallbackRef(() => {
    setLoadError(undefined);
    if (impersonatedUser) {
      impersonateUser(null);
    } else {
      selectCompany(null);
    }
  });

  useEffect(() => {
    // Loads the identity of the impersonated user, which the interceptors send in the header, or of the selected company,
    // again on every reload, or applies the logged user without a selection. A failed load puts the app behind a page
    // that offers to reload the identity or to return to the own one; a 401 never shows that page, since the interceptors
    // revert the impersonation or, without one, end the session. A load whose selection is replaced, or whose session
    // ends, discards its outcome.
    let isSelectionCurrent = true;
    const handleLoadFailure = (error: unknown) => {
      const identityError = parseError(error, 'getCurrentUser');
      if (isSelectionCurrent && identityError?.response?.status !== ErrorCode.Unauthorized) {
        setLoadError(identityError);
      }
    };
    if (impersonatedUser) {
      getIdentity().then((loadedIdentity) => {
        if (isSelectionCurrent) {
          handleImpersonatedIdentityLoaded(impersonatedUser, loadedIdentity);
        }
      }, handleLoadFailure);
    } else if (selectedCompany) {
      getIdentity({ selectedCompanyId: selectedCompany.id }).then((loadedIdentity) => {
        if (isSelectionCurrent) {
          handleCompanyIdentityLoaded(selectedCompany, loadedIdentity);
        }
      }, handleLoadFailure);
    } else if (loggedUserIdentity) {
      applySelectionIdentity(null, loggedUserIdentity);
    }

    return () => {
      isSelectionCurrent = false;
    };
  }, [
    applySelectionIdentity,
    getIdentity,
    handleCompanyIdentityLoaded,
    handleImpersonatedIdentityLoaded,
    impersonatedUser,
    loadAttempt,
    loggedUserIdentity,
    selectedCompany,
  ]);

  if (loadError) {
    return (
      <IdentityErrorPage
        error={loadError}
        exitLabel={impersonatedUser ? t('common:BACK_TO_OWN_ACCOUNT') : t('common:BACK_TO_OWN_COMPANY')}
        onExit={handleBackToOwnIdentityClick}
        onReload={handleReloadClick}
      />
    );
  }

  if (!isIdentityReady) {
    return <Loader />;
  }

  return <>{children}</>;
});
